import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyDeepmindMode({ brand, today, Copy }: ModeRendererProps) {
  // DeepMind gradient: learning blue to discovery green
  const deepmindGradient = (
    <defs>
      <linearGradient id="deepmindGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0F9D58" />
        <stop offset="50%" stopColor="#4285F4" />
        <stop offset="100%" stopColor="#00BFA5" />
      </linearGradient>
      <radialGradient id="learningGlow" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#00BFA5" stopOpacity={0.6} />
        <stop offset="100%" stopColor="#0F9D58" stopOpacity={0} />
      </radialGradient>
    </defs>
  );

  // Phase portraits: learning dynamics
  const phasePortraits: JSX.Element[] = [];
  for (let i = 0; i < 20; i++) {
    const angle = (i * Math.PI * 2) / 20;
    const radius = 180;
    const cx = 397 + radius * Math.cos(angle);
    const cy = 561.5 + radius * Math.sin(angle);
  const spiralPoints: string[] = [];
    for (let j = 0; j < 12; j++) {
      const r = 5 + j * 3;
      const a = angle + (j * Math.PI) / 6;
      spiralPoints.push(`${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`);
    }
    phasePortraits.push(
      <polyline
        key={`phase-${i}`}
        points={spiralPoints.join(" ")}
        fill="none"
        stroke="url(#deepmindGrad)"
        strokeWidth={1.5}
        opacity={0.4}
      />
    );
  }

  // Policy grids: reinforcement learning action space
  const policyGrids: JSX.Element[] = [];
  for (let row = 0; row < 14; row++) {
    for (let col = 0; col < 10; col++) {
      const x = 100 + col * 60;
      const y = 120 + row * 65;
      const value = Math.sin(row * 0.5) * Math.cos(col * 0.4) * 0.5 + 0.5;
      policyGrids.push(
        <g key={`policy-${row}-${col}`}>
          <rect
            x={x - 20}
            y={y - 20}
            width={40}
            height={40}
            fill="url(#learningGlow)"
            opacity={value * 0.5}
            rx={4}
          />
          {/* Action arrow */}
          <line
            x1={x}
            y1={y}
            x2={x + Math.cos(row + col) * 15}
            y2={y + Math.sin(row + col) * 15}
            stroke="url(#deepmindGrad)"
            strokeWidth={2}
            opacity={0.6}
          />
        </g>
      );
    }
  }

  // Convergence spirals: optimization paths
  const convergenceSpirals: JSX.Element[] = [];
  for (let i = 0; i < 8; i++) {
    const startAngle = (i * Math.PI * 2) / 8;
    const points = [];
    for (let j = 0; j < 24; j++) {
      const angle = startAngle + (j * Math.PI) / 12;
      const radius = 250 - j * 10;
      points.push(`${397 + radius * Math.cos(angle)},${561.5 + radius * Math.sin(angle)}`);
    }
    convergenceSpirals.push(
      <polyline
        key={`spiral-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke="url(#deepmindGrad)"
        strokeWidth={2}
        opacity={0.3}
      />
    );
  }

  // Reward landscape: value function surface
  const rewardLandscape: JSX.Element[] = [];
  for (let i = 0; i < 16; i++) {
    const y = 100 + i * 58;
    const points = [];
    for (let j = 0; j < 13; j++) {
      const x = 70 + j * 55;
      const reward = Math.sin(i * 0.4) * Math.cos(j * 0.3) * 35;
      points.push(`${x},${y + reward}`);
    }
    rewardLandscape.push(
      <polyline
        key={`reward-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke="url(#deepmindGrad)"
        strokeWidth={1}
        opacity={0.3}
      />
    );
  }

  // Central learning core
  const learningCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={100} fill="url(#learningGlow)" />
      <circle cx={397} cy={561.5} r={70} fill="none" stroke="#00BFA5" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={50} fill="none" stroke="#4285F4" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={30} fill="none" stroke="#0F9D58" strokeWidth={2} />
      <text
        x={397}
        y={575}
        fontFamily="monospace"
        fontSize={28}
        fontWeight="bold"
        fill="#FFFFFF"
        textAnchor="middle"
      >
        DM
      </text>
    </g>
  );

  return (
    <g>
      {deepmindGradient}
      {rewardLandscape}
      {policyGrids}
      {phasePortraits}
      {convergenceSpirals}
      {learningCore}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">{Copy?.title}</text>
        <text x={0} y={22} fontSize={14}>{Copy?.subtitle}</text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>{Copy?.date}</text>
      </g>
    </g>
  );
}
